<template>
	<div class="container page-title-area">
		<section class="mt-5">
			<div class="title d-flex justify-content-between align-items-center mb-4">
				<div class="d-flex align-items-center justify-content-start">
					<h2 class="page-title mb-0 mr-2">Pre-Recorded Streams</h2>
					<div class="tooltip-area" v-if="streamFilter.length && !loadingStatus">
						<b-badge variant="dark-3" id="stream_count_scheduled" class="stream-count text-500"
							pill>{{ streamFilter.length }}{{ getStreamnumber() }} </b-badge>
						<b-tooltip target="stream_count_scheduled" placement="top" class="wide_tooltip">
							<span class="text-dark-8 text-small">
								You have created {{ streamFilter.length }}{{ getStreamnumber() }} pre-recorded streams. Need more?
								<a class="text-underline text-white" target="_blank" href="/app/subscribe">Upgrade here</a>
							</span>
						</b-tooltip>
					</div>
				</div>
				<b-button v-if="!loadingStatus" variant="primary" size="lg"
					:id="cannotManageStreams ? 'member-disabled_new_prerecorded_stream' : ''" @click="showCreateStreamModal">
					<b-img :src="require('@/assets/images/cam-plus.svg')" alt="Pre-recorded Stream" class="mr-2" />
					New Pre-recorded Stream
				</b-button>
				<b-tooltip target="member-disabled_new_prerecorded_stream" placement="top" v-if="memberPermission">
					<span class="text-dark-8 text-small">
						You don’t have permission. Contact the team owner at <a class="text-dark-8 text-small"
							:href="'mailto:' + userEmail">{{ userEmail }}</a> to gain access.</span>
				</b-tooltip>
			</div>
		</section>
		<Spinner v-if="loadingStatus || !this.userloaded || !userSubscription" text="Retrieving data..."
			classes="text-dark-8 mh-60" spinner-color="var(--c-dark-8)" spinner-size="15px" />
		<div v-else-if="!this.noAccess">
			<section class="service-tab">
				<div class="toolbar-container tab-settings">
					<div class="d-flex w-100 align-items-center">
						<div class="mr-2 w-100 filter-wrapper">
							<b-dropdown class="w-100" variant="dark-3" size="md" no-caret
								toggle-class="d-flex align-items-center justify-content-between font-weight-normal"
								menu-class="dropdown-menu_md w-100">
								<template #button-content>
									<div class="d-flex align-items-center w-100">
										<icon-base width-view-box="20" icon-name="Sort By">
											<icon-sort />
										</icon-base>
										<!-- <span class="text-dark-5">Sort By:</span>  -->
										<span class="ml-1">{{ sortBy }}</span>
									</div> <i class="ml-2 fa fa-chevron-down" />
								</template>
								<b-dropdown-item :active="sortBy === 'Sort by Oldest'" @click="changeSortBy('Sort by Oldest')">
									<div class="ml-2">Sort by Oldest</div>
								</b-dropdown-item>
								<b-dropdown-item :active="sortBy === 'Sort by Newest'" @click="changeSortBy('Sort by Newest')">
									<div class="ml-2">Sort by Newest</div>
								</b-dropdown-item>
								<b-dropdown-item :active="sortBy === 'A to Z'" @click="changeSortBy('A to Z')">
									<div class="ml-2">A to Z</div>
								</b-dropdown-item>
								<b-dropdown-item :active="sortBy === 'Z to A'" @click="changeSortBy('Z to A')">
									<div class="ml-2">Z to A</div>
								</b-dropdown-item>
							</b-dropdown>
						</div>
						<b-input-group size="md" :class="{ active: searchWidgetActive }" class="search-form">
							<template #prepend>
								<b-input-group-text class="search-form__toggle color-light" @click="toggleSearchWidget">
									<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path fill-rule="evenodd" clip-rule="evenodd"
											d="M8.11096 15.2219C12.0382 15.2219 15.2219 12.0382 15.2219 8.11096C15.2219 4.18368 12.0382 1 8.11096 1C4.18368 1 1 4.18368 1 8.11096C1 12.0382 4.18368 15.2219 8.11096 15.2219Z"
											stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
										<path d="M16.9994 16.9994L13.1328 13.1328" stroke="currentColor" stroke-width="1.5"
											stroke-linecap="round" stroke-linejoin="round" />
									</svg>
								</b-input-group-text>
							</template>
							<b-form-input ref="searchWidgetInputElement" v-model="searchWidgetInput"
								:placeholder="searchWidgetActive ? 'Search for streams ..' : ''" class="search-form__field"
								@focus="onSearchWidgetFocus" @blur="onSearchWidgetBlur"></b-form-input>
						</b-input-group>
						<b-button-group>
							<b-button variant="dark-3" :class="{ active: !shouldShowCompactModeStreams }" class="view-mode"
								@click="unsetCompactMode">
								<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
									<rect x="2" y="2" width="7.25" height="7.25" rx="2" fill="white" />
									<rect x="2" y="10.75" width="7.25" height="7.25" rx="2" fill="white" />
									<rect x="10.75" y="2" width="7.25" height="7.25" rx="2" fill="white" />
									<rect x="10.75" y="10.75" width="7.25" height="7.25" rx="2" fill="white" />
								</svg>
							</b-button>
							<b-button variant="dark-3" :class="{ active: shouldShowCompactModeStreams }" class="view-mode"
								@click="setCompactMode">
								<svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M6 1H19" stroke="currentColor" stroke-width="2" stroke-linecap="round"
										stroke-linejoin="round" />
									<path fill-rule="evenodd" clip-rule="evenodd"
										d="M1 2C1.55228 2 2 1.55228 2 1C2 0.447715 1.55228 0 1 0C0.447715 0 0 0.447715 0 1C0 1.55228 0.447715 2 1 2Z"
										fill="currentColor" />
									<path fill-rule="evenodd" clip-rule="evenodd"
										d="M1 8C1.55228 8 2 7.55228 2 7C2 6.44772 1.55228 6 1 6C0.447715 6 0 6.44772 0 7C0 7.55228 0.447715 8 1 8Z"
										fill="currentColor" />
									<path fill-rule="evenodd" clip-rule="evenodd"
										d="M1 14C1.55228 14 2 13.5523 2 13C2 12.4477 1.55228 12 1 12C0.447715 12 0 12.4477 0 13C0 13.5523 0.447715 14 1 14Z"
										fill="currentColor" />
									<path d="M6 7H19" stroke="currentColor" stroke-width="2" stroke-linecap="round"
										stroke-linejoin="round" />
									<path d="M6 13H19" stroke="currentColor" stroke-width="2" stroke-linecap="round"
										stroke-linejoin="round" />
								</svg>
							</b-button>
						</b-button-group>
					</div>
				</div>
				<b-row v-if="showBanner" class="mt-3">
					<b-col>
						<div class="overusage-warning d-flex align-items-start justify-content-start">
							<img src="@/assets/images/info-small.svg" alt="Menu close" class="mr-2">
							<div class="overusage-text">
								<h5 class="text-dark-8 text-s-m text-500">You're using {{ this.liveCounter }} streams, but your plan only
									allows {{ getStreamnumber(true) }}.</h5>
								<p class="mb-0 text-dark-7 text-s-s font-normal">Please remove extra streams within {{ this.warningDays }}
									days, otherwise we'll need to disable them. To use {{ this.liveCounter }} streams, consider <a
										href="/app/subscribe">upgrading</a> your plan. <br>Thanks for your cooperation!</p>
							</div>
						</div>
					</b-col>
				</b-row>

				<div :class="{ 'list-view': shouldShowCompactModeStreams }" class="castr-tabs mt-5" v-if="!loadingStatus">
					<div v-for="categoryName in streamCategoryList" :key="categoryName"
						:class="{ compact: !categoryCollapseHash[categoryName] }" class="castr-tab-single mt-3">
						<b-row>
							<b-col v-for="stream in streamFilter" cols="12" :md="shouldShowCompactModeStreams ? '12' : '6'"
								:lg="shouldShowCompactModeStreams ? '12' : '4'" :key="stream._id" class="grid-item">
								<stream-card-view :stream="stream" :compact-mode="shouldShowCompactModeStreams"
									:notAllowed="streamFilterenabled.length >= getStreamnumber(true)" @delete-stream="onStreamDeleteRequest"
									@pulse-update="updateStreamPulse" />
							</b-col>
						</b-row>
					</div>
				</div>


				<b-row>
					<b-col v-if="!streamFilter.length" class="text-center">
						<div class="no-stream-style2">
							<img class="mb-4 mx-auto" width="250" src="@/assets/images/no-stream-02.svg" alt="No Stream">
						</div>
						<h3>There are no pre-recorded streams here!</h3>
						<p class="mb-3">
							Fortunately, it’s very easy to create one
						</p>
						<div class="d-flex flex-column mw-260 mx-auto">
							<b-button class="mb-2" target="_blank" variant="outline-secondary" size="lg" v-b-modal.modal-demo-video>
								<svg class="mr-2" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"
									fill="none">
									<path fill-rule="evenodd" clip-rule="evenodd"
										d="M6.59996 4.00002C6.59996 2.95068 7.45061 2.10002 8.49996 2.10002C9.5493 2.10002 10.4 2.95068 10.4 4.00002C10.4 5.04937 9.5493 5.90002 8.49996 5.90002C7.45061 5.90002 6.59996 5.04937 6.59996 4.00002ZM6.00082 5.83458C5.84546 6.04628 5.66387 6.23753 5.46083 6.40353H6.54196C6.33852 6.2376 6.15653 6.04635 6.00082 5.83458ZM9.92663 6.75295C10.9208 6.23667 11.6 5.19766 11.6 4.00002C11.6 2.28794 10.212 0.900024 8.49996 0.900024C7.47332 0.900024 6.56325 1.39908 5.99909 2.16782C5.4348 1.40041 4.52554 0.902374 3.49996 0.902374C1.78787 0.902374 0.399956 2.29029 0.399956 4.00237C0.399956 5.19783 1.07663 6.23523 2.06785 6.75247C1.35169 7.12441 0.802024 7.76533 0.548507 8.54558C0.39625 9.01418 0.396453 9.56932 0.396755 10.3907L0.396782 10.5035L0.396755 10.6164C0.396453 11.4377 0.39625 11.9929 0.548507 12.4615C0.855152 13.4052 1.59507 14.1452 2.53883 14.4518C3.00743 14.6041 3.56257 14.6039 4.38396 14.6036L4.49678 14.6035H7.49678L7.60961 14.6036H7.60962H7.60962H7.60963C8.43101 14.6039 8.98614 14.6041 9.45473 14.4518C10.391 14.1476 11.1266 13.417 11.4376 12.484L13.9173 14.0338C14.65 14.4917 15.6003 13.965 15.6003 13.101V7.90526C15.6003 7.04128 14.65 6.51455 13.9173 6.97246L11.4374 8.5224C11.1807 7.75297 10.6352 7.12127 9.92663 6.75295ZM3.49996 2.10237C2.45061 2.10237 1.59996 2.95303 1.59996 4.00237C1.59996 5.05172 2.45061 5.90237 3.49996 5.90237C4.5493 5.90237 5.39996 5.05172 5.39996 4.00237C5.39996 2.95303 4.5493 2.10237 3.49996 2.10237ZM1.68977 12.0907C1.60418 11.8272 1.59678 11.4809 1.59678 10.5035C1.59678 9.52619 1.60418 9.17982 1.68977 8.9164C1.87772 8.33797 2.33122 7.88447 2.90965 7.69653C3.17307 7.61094 3.51944 7.60353 4.49678 7.60353H7.49678C8.47412 7.60353 8.82049 7.61094 9.08391 7.69653C9.66235 7.88447 10.1158 8.33797 10.3038 8.9164C10.3894 9.17982 10.3968 9.52619 10.3968 10.5035C10.3968 11.4809 10.3894 11.8272 10.3038 12.0907C10.1158 12.6691 9.66235 13.1226 9.08391 13.3105C8.82049 13.3961 8.47412 13.4035 7.49678 13.4035H4.49678C3.51944 13.4035 3.17307 13.3961 2.90965 13.3105C2.33122 13.1226 1.87772 12.6691 1.68977 12.0907ZM11.6003 9.83569L14.4003 8.08569V12.9206L11.6003 11.1706V9.83569Z"
										fill="#8794B5" />
								</svg>
								Pre-recorded Stream Demo
							</b-button>
							<b-button @click="showCreateStreamModal" variant="primary" size="lg"
								:id="cannotManageStreams ? 'member-disabled_new_prerecorded_stream' : ''">
								<svg class="mr-2" width="22" height="25" viewBox="0 0 22 25" fill="none"
									xmlns="http://www.w3.org/2000/svg">
									<path fill-rule="evenodd" clip-rule="evenodd"
										d="M8.38098 10.7328L13.6192 13.8757L8.38098 17.0186V10.7328Z" stroke="white" stroke-width="1.5"
										stroke-linecap="round" stroke-linejoin="round" />
									<path
										d="M11 4.05412C16.4346 4.05412 20.8216 8.44111 20.8216 13.8757C20.8216 19.3104 16.4346 23.6974 11 23.6974"
										stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
									<path d="M10.9998 23.6976C5.56521 23.6976 1.17822 19.3106 1.17822 13.8759" stroke="white"
										stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="0 6" />
									<path d="M1.17822 13.8757C1.17822 8.44111 5.56521 4.05412 10.9998 4.05412" stroke="white"
										stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="0 6" />
									<path d="M13.6193 7.0007L10.7383 4.05421L13.6193 1.17321" stroke="white" stroke-width="1.5"
										stroke-linecap="round" stroke-linejoin="round" />
								</svg>
								New Pre-recorded Stream
							</b-button>
						</div>
					</b-col>
				</b-row>

				<add-schedule-modal @new-channel="onNewStream" />
				<confirm-modal modal-type="danger" message="Would you like to delete this stream and all of its content?"
					@modal-confirm="onStreamDeleteConfirm" />
				<confirm-modal message="Please update your subscription to get more concurrent stream." modal-id="increase-limit"
					modal-type="danger" ok-text="Yes" cancel-text="Cancel" @modal-confirm="gotoUpgrade" />
				<alert-modal description="Access required" modal-id="disabled-paused"
					:message="'Access required! You don’t have access to the &quot;' + (this.$store.state.User.ownermember && (Object.keys(this.$store.state.User.ownermember).length > 0) && this.$store.state.User.ownermember.email) + '&quot;. Contact the team’s organizer (' + (this.$store.state.User.ownermember && (Object.keys(this.$store.state.User.ownermember).length > 0) && this.$store.state.User.ownermember.owner.name) + ') for details.'"
					ok-text="Got It" @modal-confirm="redirect" />
			</section>
		</div>
		<div class="no-access mt-5" v-else>
			<div class="title d-flex justify-content-between align-items-center">
				<h2 class="page-title mb-3">Pre-Recorded Streams</h2>
			</div>
			<div class="text-center pt-5 mt-5">
				<h4 class="text-dark-9 text-s-xl text-500 mb-1">You don’t have permission to access this.</h4>
				<p class="text-dark-6 text-s-m">Contact the team owner at <a class="text-dark-6 text-s-m text-underline"
						:href="'mailto:' + userEmail">{{ userEmail }}</a> to gain access.</p>
			</div>
		</div>
		<demo-video-modal videoid="GFvZ8ttEszBeIHGAPbDsx" />
	</div>
</template>

<script>
import _ from 'lodash';
import Spinner from '@/components/ui/Spinner.vue';
import { mapGetters } from 'vuex';
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconSort from '@/components/icon/IconSort.vue';
import IconReorder from '@/components/icon/IconReorder.vue';
import StreamService from '@/services/StreamService';
import StreamStatsService from '@/services/StreamStatsService';
import Utils from '@/utils';
import StreamCardView from '@/components/ui/StreamCardView.vue';
import AddScheduleModal from '@/components/modals/AddScheduleModal.vue';
import StreamSchedulingConfigurator from '@/views/SchedulerDash/StreamSchedulingMode.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import SubscriptionService from '@/services/SubscriptionService';
import AlertModal from "@/components/modals/AlertModal.vue";
import Vue from 'vue';

export default {
	name: 'SchedulerDashboard',

	components: {
		Spinner,
		StreamCardView,
		AddScheduleModal,
		StreamSchedulingConfigurator,
		ConfirmModal,
		IconBase,
		IconReorder,
		DemoVideoModal,
		IconSort,
		AlertModal
	},
	data() {
		return {
			showBanner: false,
			liveCounter: 0,
			downgradeDatedifference: 0,
			warningDays: 3,
			currentCategory: 'scheduled',
			withinLast3Days: false,
			startDisabling: false,
			searchWidgetActive: false,
			noAccess: false,
			searchWidgetFocused: false,
			searchWidgetInput: '',
			userSubscription: null,
			deleteModalConfiguredStream: null,
			loading: true,
			streams: [],
			streamCreateDropdownActive: false,
			rawStreams: [],
			categoryNameMaps: {
				scheduled: 'Scheduled Stream'
			},
			categoryCollapseHash: {
				scheduled: true
			},
			sortBy: 'Sort by Oldest'
		};
	},
	computed: {
		...mapGetters({
			loadingStatus: "Ui/loadingStatus",
			scheduledStreams: "Streams/scheduledStreams",
			baseSubscription: 'User/baseSubscription',
			addonSubscriptions: 'User/addonSubscriptions'
		}),
		userloaded() {
			return this.$store.state.User.userloaded;
		},
		activeTeam() {
			return this.$store.state.User.activatedTeam;
		},
		memberPermission() {
			return this.activeTeam && this.$store.state.User.memberPermission;
		},
		cannotManageStreams() {
			if (!this.activeTeam || !this.memberPermission) return false;
			return !this.memberPermission.preRecordedStreamManage;
		},
		userEmail() {
			return this.$store.state.User.email;
		},
		getVODList() {
			return (categoryName) => {
				const filteredStreams = this.streams.filter((s) => {
					let bool = s.type === categoryName;
					if (bool && this.searchWidgetInput) {
						const searchStr = this.searchWidgetInput.toLowerCase();
						bool = s.name.toLowerCase().includes(searchStr);
					}
					return bool;
				});
				return filteredStreams;
			};
		},
		shouldShowCompactModeStreams() {
			return this.$store.state.Ui.shouldShowCompactModeStreams;
		},
		streamCategoryList() {
			const cats = {};
			this.streams.forEach(s => {
				if (s.type === 'scheduled')
					cats[s.type] = s.sortOrder;
			});

			let categoryList = Object.keys(cats).sort(
				(cat1, cat2) => cats[cat1] > cats[cat2]
			);
			categoryList = _.sortBy(categoryList, cat => cats[cat]);

			return categoryList;
		},
		activeStreamsCount() {
			const activeStreams = this.streams.filter(s => {
				return s.pulse && s.pulse.alive === true;
			});
			return activeStreams.length;
		},
		inactiveStreamsCount() {
			const inactiveStreams = this.streams.filter(s => {
				return !s.pulse || s.pulse.alive !== true;
			});
			return inactiveStreams.length;
		},
		streamFilter() {
			const filteredStreams = this.scheduledStreams.filter(
				item => {
					const searchStr = this.searchWidgetInput.toLowerCase();
					const result = item.name.toLowerCase().includes(searchStr);
					return result
				}
			)
			return filteredStreams
		},
		streamFilterenabled() {
			const filteredStreams = this.scheduledStreams.filter(
				item => {
					const searchStr = this.searchWidgetInput.toLowerCase();
					const result = item.name.toLowerCase().includes(searchStr) && item.enabled;
					return result
				}
			)
			return filteredStreams
		},
		activeTeam() {
			return this.$store.state.User.activatedTeam;
		},

	},
	watch: {
		async userloaded() {
			const permission = await this.checkPermissions();
			if (!this.activeTeam || permission) {
				this.noAccess = false;
			} else {
				this.noAccess = true;
			}
			// this.loading = false;
			this.$store.dispatch('Ui/setLoadingStatus', false);
		},
	},
	async mounted() {
		this.$store.dispatch('Ui/setLoadingStatus', true);
		await this.$store.dispatch('Streams/getStreams');
		// await this.$store.dispatch('User/getinfoOwner', null, { root: true });
		this.loadStreams();
		// event tracking
		this.$root.$on('teamchanging', data => {
			// this.loading = true;
			this.$store.dispatch('Ui/setLoadingStatus', true);
		});
		this.$root.$on('teamchanged', data => {
			const redirect = this.checkRedirect();
			if (redirect) {
				this.$router.push({ path: '/' });
			}
			this.loadStreams();
		});
		// update window title
		this.$emit('updateTitle', 'Dashboard - Castr Streams');
		this.$root.$on('toggledone', data => {
			this.checkAllowed('scheduled');
		});
		this.$root.$on('countDownends', data => {
			this.checkAllowed('scheduled');
		});
		this.onViewLoaded();
	},
	methods: {
		async checkAllowed(categoryName) {
			// 1. If have excess stream enabled this function will count excess 
			// and disable those automatically.
			//2. If livestream count is greater than the allowed stream then we will show
			// a warning banner for 3 days.
			//3. After 3 days we will disable the livstream too.
			let list = this.streamFilter;
			if (list.length > 0) {
				let check = list.filter(s => {
					return s.enabled;
				});
				let enabledStreamlength = check.length;
				let liveCollection = [];
				if (!this.startDisabling && (enabledStreamlength > this.getStreamnumber(categoryName, true))) {
					let live = await Promise.all(list.map(async s => {
						if (s.enabled) {
							let pulse = await this.checkPulse(s.key);
							if (pulse.alive) {
								if (!liveCollection.includes(s._id)) {
									liveCollection.push(s._id);
								}
								let objWithIdIndex = check.findIndex((obj) => obj._id === s._id);
								if (objWithIdIndex > -1) {
									check.splice(objWithIdIndex, 1);
								}
								this.liveCounter = liveCollection.length;
							}
						}
					}));
					if (this.liveCounter > this.getStreamnumber(categoryName, true)) {
						// const oneDay = 5 * 60 * 1000; 
						const oneDay = 24 * 60 * 60 * 1000;
						let d = new Date();
						let utc = d.getTime();
						let current = utc;
						let start = new Date('Jul 12 2023 10:00:00 GMT+0000').getTime();
						let dayspassed = Math.floor((current - start) / oneDay);
						if (this.withinLast3Days || ((dayspassed < 3) && (dayspassed >= 0))) {
							this.currentCategory = categoryName;
							if (this.withinLast3Days) {
								this.warningDays = 3 - this.downgradeDatedifference;
							}
							if (dayspassed < 3) {
								this.warningDays = 3 - dayspassed;
							}
							this.showBanner = true;
						} else {
							this.warningDays = 0;
							this.startDisabling = true;
							this.showBanner = false;
							this.checkAllowed(categoryName);
						}
					} else {
						this.showBanner = false;
					}
				} else {
					this.showBanner = false;
				}
				check.sort((a, b) => a.creationTime - b.creationTime);
				if (enabledStreamlength > this.getStreamnumber(categoryName, true)) {
					let toReduce = (enabledStreamlength - this.getStreamnumber(categoryName, true));
					if ((toReduce > 0) && (check.length > 0)) {
						await this.onStreamToggleConfirm(null, false, check[0]);
					}
				}
				if (enabledStreamlength >= this.getStreamnumber(categoryName, true)) {
					localStorage.setItem('notAllowedscheduled', true);
				} else {
					localStorage.setItem('notAllowedscheduled', false);
				}
			}
		},
		async checkPulse(streamKey) {
			try {
				const pulseObject = await StreamStatsService.getStreamMediaPulse(
					streamKey
				);
				return pulseObject;
			} catch (e) { }
		},
		getDowngradedate() {
			if (this.userSubscription) {
				let date = [];
				if (this.userSubscription.subscription && this.userSubscription.subscription.enabled && this.userSubscription.subscription.package.category == 'live') {
					let starttime = this.userSubscription.subscription;
					date.push(new Date(starttime).getTime());
				}
				const typeFilteredaddonsub = _.map(this.userSubscription && this.userSubscription.addonSubscriptions, function (single) {
					date.push(new Date(single.cstart).getTime());

				});
				const lastActivateddate = Math.max.apply(null, date);
				if (lastActivateddate) {
					// const millisecondsPerDay = 5 * 60 * 1000;
					const millisecondsPerDay = 24 * 60 * 60 * 1000;

					const currentDate = new Date();
					const givenDate = new Date(lastActivateddate);
					const timeDifference = currentDate.getTime() - givenDate.getTime();

					const daysDifference = Math.floor(timeDifference / millisecondsPerDay);
					this.withinLast3Days = (daysDifference < 3);
					this.downgradeDatedifference = daysDifference;
				}
			}
		},
		async onStreamToggleConfirm(ev, newStatus, s = null) {
			let stream = this.stream;
			if (s) {
				stream = s;
			}
			try {
				await StreamService.toggleStream(stream._id);
				this.loadStreams();
				// window.location.reload();
			} catch (err) {
				this.$notify({
					group: 'error',
					title: "Couldn't toggle stream status",
					text: err.message
				});

				if (err.message && err.message.indexOf('upgrade') > -1) {
					this.$root.$emit('bv::show::modal', 'billing-prompt');
				}
			}
		},
		checkPermissions() {
			return this.memberPermission && (this.memberPermission.preRecordedStreamManage || this.memberPermission.preRecordedStreamToggle || this.memberPermission.preRecordedStreamPlatformToggle || this.memberPermission.preRecordedStreamDelete);
		},
		showCreateStreamModal() {
			if (this.cannotManageStreams) return;
			this.$bvModal.show('modal-add-schedule');
		},
		async loadStreams() {
			try {
				const streams = await StreamService.getUserStreams();
				this.userSubscription = { subscription: this.baseSubscription, addonSubscriptions: this.addonSubscriptions };
				setTimeout(async () => {
					this.streams = streams;
					if (!this.streamFilter.length) {
						// this.loading = true;
						this.$store.dispatch('Ui/setLoadingStatus', true);
						const permission = await this.checkPermissions();
						if (!this.activeTeam || permission) {
							this.noAccess = false;
						} else {
							this.noAccess = true;
						}
						// this.loading = false;
						this.$store.dispatch('Ui/setLoadingStatus', false);
					} else {
						// this.loading = false;
						this.$store.dispatch('Ui/setLoadingStatus', false);
					}

				}, 200);
				setTimeout(() => {
					this.checkAllowed('scheduled');
					this.getDowngradedate();
				}, 1000);
			} catch (err) {
				this.$notify({ group: 'error', title: err.error, text: err.message });
				// this.loading = false;
				this.$store.dispatch('Ui/setLoadingStatus', false);
				return;
			}

			const streamsCount = _.size(this.streams);
			// event tracking
			window.trackEvent('Dashboard', {
				totalStreams: streamsCount,
				enabledStreams: _.size(_.filter(this.streams, { enabled: true }))
			});
			if (window.Intercom) {
				window.Intercom('update', {
					totalStreams: streamsCount,
					totalLiveStreams: this.streams.filter(({ type }) => type === 'live').length
				});
			}
		},
		getStreamnumber(number = false) {
			let scheduledPackage = this.userSubscription && this.userSubscription.addonSubscriptions.filter(function (single) {
				return single.package.category == 'scheduled'
			});
			let streamnumber = null
			let streamCountarray = [];
			if (scheduledPackage && (scheduledPackage.length > 1)) {
				const allowedStream = _.map(scheduledPackage, function (single) {
					if (single.enabled) {
						let streamCountoverride = _.get(single, ['definitionOverride', 'streams']);
						if (streamCountoverride) {
							streamCountarray.push(streamCountoverride);
						} else {
							streamnumber = _.get(single, ['package', 'definition', 'streams']);
							streamCountarray.push(streamnumber);
						}
					}
				});

			} else {
				if (scheduledPackage && (scheduledPackage.length > 0) && scheduledPackage[0].enabled) {
					let streamCountoverride = _.get(scheduledPackage[0], ['definitionOverride', 'streams']);
					if (streamCountoverride) {
						streamCountarray.push(streamCountoverride);
					} else {
						streamnumber = _.get(scheduledPackage[0], ['package', 'definition', 'streams']);
						streamCountarray.push(streamnumber);
					}
				}
			}

			if (streamCountarray.length) {
				if (number) {
					return (streamCountarray.length > 1 ? Math.max.apply(Math, streamCountarray) : streamCountarray[0]);
				} else {
					return '/' + (streamCountarray.length > 1 ? Math.max.apply(Math, streamCountarray) : streamCountarray[0]);
				}
			}
		},
		changeSortBy(val) {
			this.sortBy = val;
			this.$store.dispatch('Streams/setSortOption', this.sortBy)
		},
		setCompactMode() {
			this.$store.dispatch('Ui/setCompactModeStreams', true);
		},
		unsetCompactMode() {
			this.$store.dispatch('Ui/setCompactModeStreams', false);
		},
		updateStreamPulse(streamId, pulseData) {
			const sindex = _.findIndex(this.streams, s => s._id === streamId);
			if (sindex > -1) {
				const updatedStream = { ...this.streams[sindex], pulse: pulseData };
				this.streams = Utils.updateArrayItem(
					this.streams,
					updatedStream,
					sindex
				);
			}
		},
		gotoUpgrade() {
			window.location.href = `/app/subscribe?category=live`;
		},
		toggleSearchWidget() {
			this.searchWidgetActive = !this.searchWidgetActive;
			this.$refs.searchWidgetInputElement.focus();
			if (!this.searchWidgetActive) {
				this.searchWidgetInput = '';
			}
		},
		onSearchWidgetFocus() {
			this.searchWidgetFocused = true;
		},
		onSearchWidgetBlur() {
			this.searchWidgetFocused = false;
			setTimeout(() => {
				const blankSearchVal =
					!this.searchWidgetInput || !this.searchWidgetInput.length;
				if (
					this.searchWidgetActive &&
					blankSearchVal &&
					!this.searchWidgetFocused
				) {
					this.toggleSearchWidget();
				}
			}, 500);
		},
		onViewLoaded() {
			const qs = Utils.parseQueryString(window.location.search) || {};
			if (qs.action === 'newstream') {
				let streamCreationType = qs.streamtype || '';
				streamCreationType =
					streamCreationType === 'restream' ? '' : streamCreationType;
				if (streamCreationType) streamCreationType += '-';

				const streamCreationModalId = `modal-add-${streamCreationType}channel`;

				this.$root.$emit('bv::show::modal', streamCreationModalId);
			}
		},
		openCamModal() {
			this.$root.$emit('bv::show::modal', 'modal-add-cam-channel');
		},
		openScheduledStreamModal() {
			this.$root.$emit('bv::show::modal', 'modal-add-scheduled-channel');
		},
		onStreamCreateToggle(state) {
			this.streamCreateDropdownActive = state;
		},
		onNewStream(stream, regionDetails) {
			this.streams = [...this.streams, stream];
			this.$notify({ group: 'success', text: 'Stream deployed successfully' });

			// setTimeout(() => {
			let redirectPath = '/multistreams/';
			if (stream.type === 'live') {
				redirectPath = '/livestreams/';
			} else if (stream.type === 'ipcam') {
				redirectPath = '/ipcams/';
			} else if (stream.type === 'scheduled') {
				redirectPath = '/scheduled/';
			} else if (stream.type === 'vod') {
				redirectPath = '/vods/';
			}

			redirectPath += stream._id;
			this.$router.push({ path: redirectPath });
			// }, 3000)

			// track event
			window.trackEvent(`Deployed new stream ${stream.name}${regionDetails ? ` in ${regionDetails.name}` : ''}`, stream);
		},
		onStreamDeleteRequest(stream) {
			this.deleteModalConfiguredStream = stream;
			this.$root.$emit('bv::show::modal', 'modal-confirm');
		},
		async onStreamDeleteConfirm() {
			// pop stream out of the list
			const index = this.streams.indexOf(this.deleteModalConfiguredStream);
			// this.streams = _.concat(
			// 	this.streams.slice(0, index),
			// 	this.streams.slice(index + 1)
			// );

			try {
				await StreamService.deleteStream(this.deleteModalConfiguredStream._id);

				// track event
				const removedStream = this.deleteModalConfiguredStream;
				window.trackEvent(
					`Deleted stream ${removedStream.name}`,
					removedStream
				);
				await this.$store.dispatch('Streams/getStreams');
				this.$notify({ group: 'info', text: 'The stream was deleted' });
			} catch (err) {
				this.$notify({ group: 'error', title: err.error, text: err.message });
				// push stream back to list
				this.onNewStream(this.deleteModalConfiguredStream);
			}

			this.deleteModalConfiguredStream = null;
		},
		// async setdefaultTeam(){
		// 	const res = await UserService.selectTeam(this.$store.state.User._id);
		// 	console.log(res);
		// const setOwner = await UserService.setOwnerSession(null);
		// },
		redirect() {
			setTimeout(() => {
				this.$router.push({ path: '/' });
				window.location.reload();
			}, 1000);
		},
		checkRedirect() {
			let parts = window.location.pathname.split('/');
			let redirect = false;
			parts.map(function (p) {
				if ((p == 'multistreams') || (p == 'scheduled') || (p == 'videos') || (p == 'livestreams')) {
					redirect = true;
				}
			});
			return redirect;
		},
	}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.search-form__toggle {
	border: none;
	background-color: transparent;
	transition: all .3s ease-in-out;
	position: absolute;
	top: 5px;
}

.search-form .input-group-append {
	background-color: transparent;
}

.active .search-form__toggle {
	opacity: 1;
	transition: all .3s ease-in-out;
	z-index: 9;
}

.search-form__field {
	border: none;
	width: 0;
	flex: 0 0 0;
	transition: all .2s ease-out;
	padding-left: 0;
	padding-right: 0;
	caret-color: var(--c-main-light);
}

.input-group>.search-form__field {
	border-radius: 6px;
}

.active .search-form__field {
	width: auto;
	flex: 1 1 auto;
	max-width: 620px;
	transition: all .2s ease-out;
	padding-left: 40px;
	padding-right: 16px;
	border: 1.5px solid var(--c-main-light);
	background: var(--c-dark-2);
}

.search-form__close {
	visibility: hidden;
}

.active .search-form__close {
	visibility: visible;
}

.view-mode.active {
	color: var(--c-text-1);
}

.castr-card.compact {
	display: flex;
	background: transparent;
	align-items: center;
}

.no-stream-style2 {
	min-height: 219px;
}

.stream-count {
	padding: 0px 7px;
	width: auto;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	line-height: 13px;
}

.overusage-warning {
	background: rgba(167, 181, 226, 0.08);
	border-radius: 6px;
	padding: 8px 12px;
}
</style>
